import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth.jsx'
import { useDashboard } from '../hooks/useDashboard.js'
import { useNotificationCenter } from '../contexts/NotificationContext.jsx'
import AppHeader from '../shared/components/AppHeader.jsx'
import { bidWorkupService, timekeepingService } from '../shared/api'
import { describeFailure } from '../shared/api/failure'
import { parseUTC, formatLocalDateTime, startOfWeekLocal, addDaysLocal, toLocalISODate } from '../utils/dateUtils'
import { routeForNotification } from '../shared/notifications/notificationLink'
import { FEATURE_GATED, grantedModuleIds, isModuleReleased } from '../shared/constants/modules.js'
import { useTenantBranding } from '../contexts/TenantContext.jsx'
import { useModuleSwitches } from '../contexts/ModuleSwitchContext.jsx'
import { useLanguage, useT } from '../shared/i18n/LanguageContext.jsx'

/**
 * What the notifications panel says when one of its two reads did not answer.
 *
 * THESE ARE ENGLISH LITERALS RATHER THAN DICTIONARY KEYS, deliberately. The sentence they sit beside
 * is the SERVER's — `describeFailure` prefers `response.data.detail`, and every one of its fallbacks
 * is an English constant in shared/api/config.js — so a Spanish label over an English sentence would
 * switch language halfway through a paragraph, which reads worse than an English pair. When these
 * move into the portal dictionary they should travel WITH a translation of those constants, not
 * ahead of one. (dict/en/portal.js and its Spanish twin are outside this change's file set.)
 *
 * `retry` / `retrying` are spelled "Retry" and "Retrying…" to match `retryOffered` and
 * `noRetryOffered` in src/test/apiFailure.js, which query by BUTTON ROLE against /retry|try again/i
 * — the two spellings this tree uses.
 */
const PANEL_FAILURE_COPY = {
  approvals: 'Approvals',
  bidWorkup: 'Bid workup',
  retry: 'Retry',
  retrying: 'Retrying…',
  // The fallbacks handed to describeFailure. Reached only when a throw carries no server sentence
  // and no `userMessage` — a non-axios failure — so they name the thing that was being read instead
  // of describing an HTTP status nobody actually got.
  reportsFallback: 'Reports awaiting approval could not be loaded.',
  bidWorkupFallback: 'Bid workup notices could not be loaded.',
  weekFallback: 'This week could not be loaded.',
  // "Loaded and empty" and "could not load" must not render the same words, and "No new
  // notifications" is a claim about BOTH sources at once. These two say only what is known.
  //
  // `partialEmpty` names the missing source as a trailing appositive rather than as the subject of a
  // clause, so one string works for a plural label ("Approvals") and a singular one ("Bid workup")
  // without a verb that agrees with only one of them.
  noneKnown: 'Nothing can be listed here — both reads above failed.',
  partialEmpty: (name) => `Nothing is waiting in the part that loaded. Not included: ${name}.`,
  // The Timekeeping KPI widget reads '—' instead of 0 when the approvals read failed; the hover says
  // why, because a dash on its own is only half an answer.
  approvalsUnknown: 'The count could not be loaded — see the Notifications panel above.',
}

/**
 * The stored session row, or an empty object.
 *
 * `JSON.parse` on a corrupted `bfs_user` throws a SyntaxError, and the reader of it below sits
 * inside a `try` that exists for HTTP failures. Letting the parse land there would report a browser
 * problem as a failed request — and worse, a SyntaxError carries no `.response`, so
 * `describeFailure` would class it `transient` and the panel would draw a Retry button that can only
 * ever fail again. Guarded here so those catches only hold what the network actually did.
 */
function readStoredUser() {
  try {
    return JSON.parse(localStorage.getItem('bfs_user') || '{}') || {}
  } catch {
    // Silent on purpose, and the reader loses nothing they could have used: an unreadable session
    // row and an absent one lead to the same place — the unfiltered request an approver would get —
    // and this screen only renders for a signed-in user, so `useAuth` still holds the identity that
    // matters. The one thing it must NOT do is reach the catches below and be described as an
    // outage.
    return {}
  }
}

/* ═══════════════════════════════════════════════════════════════════════
   KPI Widget primitives
═══════════════════════════════════════════════════════════════════════ */

function WidgetSkeleton() {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 animate-pulse">
      <div className="flex items-center gap-2 mb-4">
        <div className="h-4 w-4 bg-gray-200 rounded" />
        <div className="h-4 bg-gray-200 rounded w-28" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        {[0, 1, 2, 3].map(i => (
          <div key={i}>
            <div className="h-7 bg-gray-200 rounded w-14 mb-1" />
            <div className="h-3 bg-gray-100 rounded w-20" />
          </div>
        ))}
      </div>
    </div>
  )
}

// `hint` is a hover, used for exactly one thing: saying why a number is a dash. `value ?? '—'` below
// renders null as '—', and a dash with no explanation is only half an answer.
function Stat({ value, label, warn, hint }) {
  return (
    <div title={hint || undefined}>
      <p className={`text-2xl font-bold leading-none ${warn ? 'text-red-600' : 'text-gray-900'}`}>
        {value ?? '—'}
      </p>
      <p className="text-xs text-gray-500 mt-1">{label}</p>
    </div>
  )
}

function WidgetCard({ accentColor, icon, title, path, navigate, children }) {
  const t = useT('portal')
  return (
    <div
      className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden"
      style={{ borderTop: `3px solid ${accentColor}` }}
    >
      <div className="p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <span className="text-lg">{icon}</span>
            <h3 className="text-sm font-semibold text-gray-700">{title}</h3>
          </div>
          {path && (
            <button
              onClick={() => navigate(path)}
              className="text-xs text-blue-600 hover:text-blue-800 font-medium transition-colors"
            >
              {t('view')} →
            </button>
          )}
        </div>
        <div className="grid grid-cols-2 gap-4">{children}</div>
      </div>
    </div>
  )
}

/* ═══════════════════════════════════════════════════════════════════════
   Per-module KPI widgets
═══════════════════════════════════════════════════════════════════════ */

// `refused`: the service answered, and the answer was no. Saying "Service offline - start the backend"
// to someone whose account lacks a permission sends them looking for an outage that does not exist.
function OfflineCard({ icon, title, path, navigate, refused = false }) {
  const t = useT('portal')
  return (
    <div
      className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden"
      style={{ borderTop: '3px solid #CBD5E1' }}
    >
      <div className="p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <span className="text-lg">{icon}</span>
            <h3 className="text-sm font-semibold text-gray-400">{title}</h3>
          </div>
          {path && (
            <button onClick={() => navigate(path)} className="text-xs text-blue-600 hover:text-blue-800 font-medium">
              {t('view')} →
            </button>
          )}
        </div>
        <div className="flex items-center gap-2 py-3 text-gray-400">
          <span className="text-base">{refused ? '🔒' : '⚡'}</span>
          <div>
            <p className="text-xs font-medium">{t(refused ? 'accessRefused' : 'serviceOffline')}</p>
            <p className="text-xs text-gray-300">{t(refused ? 'accessRefusedHelp' : 'serviceOfflineHelp')}</p>
          </div>
        </div>
      </div>
    </div>
  )
}

// THE WEEK WINDOW, and why it is no longer `getWeekStart(new Date())` then `toISOString()`.
// That shape was used by every week widget in this app, and the week maths in it was correct:
// local accessors, Monday found properly. What was wrong is that the Date it produced still
// carried the TIME OF DAY, so writing it down with toISOString() slid it forward a day whenever
// the local clock was past 19:00 US Central. The window became Tue-Mon instead of Mon-Sun:
//
//   the week's own Monday DROPS OUT of the total
//   next week's Monday is counted inside it
//
// and the 40-hour overtime threshold is applied to that total. `startOfWeekLocal` returns LOCAL
// MIDNIGHT, so its value is a calendar day rather than an instant, and `toLocalISODate` is the
// only correct way to write a calendar day down. See src/utils/dateUtils.js.
//
// `offset` walks whole weeks, so it goes through addDaysLocal too: adding `offset * 7 * 86400000`
// milliseconds would be an hour out on either side of a daylight-saving changeover.
function getWeekBounds(offset = 0, locale = 'en-US') {
  const mon = addDaysLocal(startOfWeekLocal(), offset * 7)
  const sun = addDaysLocal(mon, 6)
  const label = `${mon.toLocaleDateString(locale, { month: 'short', day: 'numeric' })} – ${sun.toLocaleDateString(locale, { month: 'short', day: 'numeric' })}`
  return { from: toLocalISODate(mon), to: toLocalISODate(sun), label }
}

const APPROVAL_ROLES = ['master_admin', 'admin', 'manager', 'superintendent']

function TimekeepingWidget({ data: dashData, navigate, pendingReportsCount }) {
  const { user } = useAuth()
  const t = useT('portal')
  const tc = useT('common')
  const tm = useT('modules')
  const { lang } = useLanguage()
  const [weekOffset, setWeekOffset] = useState(0)
  const [tkData, setTkData] = useState(null)
  const [tkLoading, setTkLoading] = useState(false)
  /**
   * The week read, when it did not answer.
   *
   * `setTkData(null)` alone was the same defect as the one in the notifications panel, wearing a
   * different hat: `null` flows straight into `reports = tkData?.reports ?? []` and
   * `summary?.regular_hours ?? 0`, so a refused or timed-out week drew "0 reports, 0.0 hrs, 0.0
   * overtime" — three confident numbers about a week nobody managed to read. Holding the failure
   * lets those three say '—' and lets the widget print the reason once, underneath.
   */
  const [tkFailure, setTkFailure] = useState(null)

  const isOffline = dashData?.timekeeping === 'OFFLINE'

  const fetchWeek = useCallback(async (offset) => {
    if (isOffline) return
    setTkLoading(true)
    try {
      const { from, to } = getWeekBounds(offset, lang)
      const role = user?.role?.toLowerCase() || ''
      const isForemanRole = ['foreman', 'field_worker', 'employee'].includes(role)
      const empId = isForemanRole ? (user?.employee_id ?? user?.id) : undefined
      const [summary, reports] = await Promise.all([
        timekeepingService.getSummary({ date_from: from, date_to: to, ...(empId ? { employee_id: empId } : {}) }),
        timekeepingService.getDailyReports({ date_from: from, date_to: to, limit: 200, ...(empId ? { foreman_id: empId } : {}) }),
      ])
      setTkData({ summary, reports: reports?.items ?? [] })
      setTkFailure(null)
    } catch (err) {
      setTkData(null)
      setTkFailure(describeFailure(err, PANEL_FAILURE_COPY.weekFallback))
    } finally {
      setTkLoading(false)
    }
  }, [isOffline, user, lang])

  useEffect(() => {
    fetchWeek(weekOffset)
  }, [weekOffset, fetchWeek])

  if (dashData?.timekeeping === undefined) return <WidgetSkeleton />
  if (isOffline) return <OfflineCard icon="⏰" title={tm('timekeeping')} path="/timekeeping" navigate={navigate} />

  const summary  = tkData?.summary
  const reports  = tkData?.reports ?? []

  const role          = user?.role?.toLowerCase() || ''
  const isForeman     = ['foreman', 'field_worker', 'employee'].includes(role)
  
  // Awaiting approval is passed from parent to avoid week-filtering bugs.
  //
  // It is `null` — NOT 0 — when the read that would have produced it failed, and `|| 0` erased that
  // difference: a 500 from the daily-reports call drew a confident "0 Awaiting approval" at an
  // approver who may have a week of reports waiting on them. `Stat` renders null as '—', and the
  // Notifications panel above this widget carries the sentence that says why.
  const needApproval = pendingReportsCount ?? null

  // Hours: for foreman roles, derive crew total from report labor hours.
  //
  // Every one of these is week-scoped and therefore unknown — `null`, which `Stat` draws as '—' —
  // when the week read failed. `Awaiting approval` deliberately is NOT in this group: it comes from
  // the parent's own read of the pending queue, so a broken week does not entitle this widget to
  // stop reporting it.
  const weekUnknown = Boolean(tkFailure)
  const crewHours  = weekUnknown ? null : (isForeman
    ? reports.reduce((acc, r) => acc + (r.total_labor_hours || 0), 0)
    : (summary?.regular_hours ?? 0))
  const ot         = weekUnknown ? null : (summary?.overtime_hours ?? 0)
  const hoursLabel = isForeman ? t('crewHours') : t('regularHours')

  const { label } = getWeekBounds(weekOffset, lang)

  return (
    <WidgetCard accentColor="#3B82F6" icon="⏰" title={tm('timekeeping')} path="/timekeeping" navigate={navigate}>
      {/* Week navigation */}
      <div className="col-span-2 flex items-center justify-between bg-gray-50 rounded-lg px-2 py-1 mb-2">
        <button
          onClick={() => setWeekOffset(o => o - 1)}
          className="w-7 h-7 flex items-center justify-center rounded-md bg-white border border-gray-200 text-gray-600 hover:bg-blue-50 hover:border-blue-300 hover:text-blue-600 transition-colors shadow-sm text-sm font-bold"
          title={t('previousWeek')}
        >←</button>
        <span className="text-xs text-gray-600 font-semibold tracking-wide">
          {weekOffset === 0 ? t('thisWeek') : weekOffset === -1 ? t('lastWeek') : label}
          {weekOffset < -1 && <span className="block text-center text-gray-400 font-normal">{label}</span>}
        </span>
        <button
          onClick={() => setWeekOffset(o => Math.min(0, o + 1))}
          className={`w-7 h-7 flex items-center justify-center rounded-md border text-sm font-bold transition-colors shadow-sm ${
            weekOffset < 0
              ? 'bg-white border-gray-200 text-gray-600 hover:bg-blue-50 hover:border-blue-300 hover:text-blue-600'
              : 'bg-gray-100 border-gray-100 text-gray-300 cursor-default'
          }`}
          disabled={weekOffset >= 0}
          title={t('nextWeek')}
        >→</button>
      </div>
      {tkLoading ? (
        <div className="col-span-2 text-center py-2 text-xs text-gray-400">{tc('loading')}</div>
      ) : (
        <>
          <Stat value={weekUnknown ? null : reports.length} label={t('reportsCreated')} />
          <Stat
            value={needApproval}
            label={t('awaitingApproval')}
            warn={needApproval > 0}
            hint={needApproval === null ? PANEL_FAILURE_COPY.approvalsUnknown : undefined}
          />
          <Stat value={crewHours === null ? null : crewHours.toFixed(1)} label={hoursLabel} />
          <Stat value={ot === null ? null : ot.toFixed(1)} label={t('overtimeHours')} warn={ot > 0} />
          {tkFailure && (
            /* The reason, once, under the dashes — so the three '—' above are an answer rather than
               a shrug. Retry only where `retryable` says an immediate one could come back different,
               which is `transient` and nothing else; a 403 here gets the sentence and no button. */
            <div className="col-span-2 mt-1 pt-2 border-t border-gray-100">
              <p className="text-xs text-gray-500">{tkFailure.message}</p>
              {tkFailure.retryable && (
                <button
                  onClick={() => fetchWeek(weekOffset)}
                  className="mt-1.5 text-xs font-medium text-blue-600 hover:text-blue-800 hover:underline transition-colors"
                >
                  {PANEL_FAILURE_COPY.retry}
                </button>
              )}
            </div>
          )}
        </>
      )}
    </WidgetCard>
  )
}

function ProjectsWidget({ data, navigate }) {
  const t = useT('portal')
  const tm = useT('modules')
  if (data?.projects === undefined) return <WidgetSkeleton />
  if (data?.projects === 'OFFLINE') return <OfflineCard icon="📊" title={tm('projects')} path="/projects" navigate={navigate} />
  const raw = data?.projects
  const total = raw?.total_projects ?? 0
  const active = raw?.active_projects ?? 0
  return (
    <WidgetCard accentColor="#F97316" icon="📊" title={tm('projects')} path="/projects" navigate={navigate}>
      <Stat value={active} label={t('activeProjects')} />
      <Stat value={total} label={t('totalProjects')} />
    </WidgetCard>
  )
}

function BidWorkupWidget({ data, navigate }) {
  const t = useT('portal')
  const tm = useT('modules')
  if (data?.bidworkup === undefined) return <WidgetSkeleton />
  if (data?.bidworkup === 'OFFLINE') return <OfflineCard icon="🏗️" title={tm('bidworkup')} path="/bidworkup" navigate={navigate} />
  if (data?.bidworkup === 'REFUSED') return <OfflineCard icon="🏗️" title={tm('bidworkup')} refused navigate={navigate} />

  // The request counts: everyone who may raise a bid request but not see the bids themselves (see
  // useDashboard). Titled and linked as REQUESTS - most of these people hold `bid_request` and not
  // `bidworkup`, and the page that lists what they raised is /bidworkup/requests.
  if (data?.bidworkup?._type === 'requests') {
    const s = data.bidworkup
    return (
      <WidgetCard accentColor="#EAB308" icon="📋" title={t('bidRequests')} path="/bidworkup/requests" navigate={navigate}>
        <Stat value={s.total ?? 0}     label={t('totalRequests')} />
        <Stat value={s.pending ?? 0}   label={t('pending')} />
        <Stat value={s.assigned ?? 0}  label={t('assigned')} />
        <Stat value={s.completed ?? 0} label={t('completed')} />
      </WidgetCard>
    )
  }

  // Full bids view (estimator, manager, admin)
  const bids = Array.isArray(data?.bidworkup) ? data.bidworkup : []

  const activeBids    = bids.filter(b => ['Draft', 'In Progress', 'Submitted'].includes(b.status))
  const awardedBids   = bids.filter(b => b.status === 'Awarded')
  const lostBids      = bids.filter(b => b.status === 'Lost')
  const closedCount   = awardedBids.length + lostBids.length
  const pipelineValue = activeBids.reduce((s, b) => s + (parseFloat(b.grand_total) || 0), 0)
  const awardedValue  = awardedBids.reduce((s, b) => s + (parseFloat(b.grand_total) || 0), 0)
  const winRate       = closedCount > 0 ? Math.round(awardedBids.length / closedCount * 100) : null

  const fmtShort = (v) => {
    if (!v) return '$0'
    if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`
    if (v >= 1_000)     return `$${(v / 1_000).toFixed(0)}K`
    return `$${Math.round(v)}`
  }

  return (
    <WidgetCard accentColor="#EAB308" icon="🏗️" title={tm('bidworkup')} path="/bidworkup" navigate={navigate}>
      <Stat value={fmtShort(pipelineValue)} label={t('pipelineValue')} />
      <Stat value={fmtShort(awardedValue)}  label={t('awardedValue')} />
      <Stat value={activeBids.length}       label={t('activeBids')} />
      <Stat value={winRate != null ? `${winRate}%` : '—'} label={t('winRate')} />
    </WidgetCard>
  )
}

function HRWidget({ data, navigate }) {
  const t = useT('portal')
  const tm = useT('modules')
  if (data?.hr === undefined) return <WidgetSkeleton />
  if (data?.hr === 'OFFLINE') return <OfflineCard icon="👥" title={tm('hrShort')} path="/hr" navigate={navigate} />
  const raw    = data?.hr
  const total  = raw?.total_employees ?? raw?.total ?? (Array.isArray(raw) ? raw : (raw?.employees ?? raw?.items ?? [])).length
  const active = raw?.active_employees ?? (Array.isArray(raw) ? raw : (raw?.employees ?? raw?.items ?? [])).filter(e => !e.employment_status || e.employment_status === 'active').length
  return (
    <WidgetCard accentColor="#8B5CF6" icon="👥" title={tm('hrShort')} path="/hr" navigate={navigate}>
      <Stat value={active} label={t('activeEmployees')} />
      <Stat value={total} label={t('totalRecords')} />
    </WidgetCard>
  )
}

function CRMWidget({ data, navigate }) {
  const t = useT('portal')
  const tm = useT('modules')
  if (data?.crm === undefined) return <WidgetSkeleton />
  if (data?.crm === 'OFFLINE') return <OfflineCard icon="🤝" title={tm('crm')} path="/crm" navigate={navigate} />
  const s = data?.crm
  return (
    <WidgetCard accentColor="#6366F1" icon="🤝" title={tm('crm')} path="/crm" navigate={navigate}>
      <Stat value={s?.total_clients ?? s?.total ?? '—'} label={t('totalClients')} />
      <Stat value={s?.active_clients ?? s?.active ?? '—'} label={t('activeClients')} />
    </WidgetCard>
  )
}

function AdminUsersWidget({ data, navigate }) {
  const t = useT('portal')
  if (data?.users === undefined) return <WidgetSkeleton />
  if (data?.users === 'OFFLINE') return <OfflineCard icon="🔐" title={t('systemUsers')} path="/admin" navigate={navigate} />
  const users  = Array.isArray(data?.users) ? data.users : []
  const active = users.filter(u => u.is_active !== false).length
  return (
    <WidgetCard accentColor="#EF4444" icon="🔐" title={t('systemUsers')} path="/admin" navigate={navigate}>
      <Stat value={active} label={t('activeAccounts')} />
      <Stat value={users.length} label={t('totalRegistered')} />
    </WidgetCard>
  )
}

/* ═══════════════════════════════════════════════════════════════════════
   Module registry — single source of truth for nav tiles + role access.
   Field-operation roles are included so they get appropriate module tiles.
═══════════════════════════════════════════════════════════════════════ */

// `name` is the ENGLISH FALLBACK, not what gets drawn. The tiles render `tm(m.id) || m.name`
// against shared/i18n/dict/*/modules.js — keyed by module ID, the same dictionary AppHeader's nav
// strip reads, so a module is named once instead of twice. A module added here without a dictionary
// entry falls back to this string rather than to a blank tile; see that dictionary's header for why
// a bare `tm(m.id)` would be the one forbidden output.
const ALL_MODULES = [
  {
    id: 'identity',
    name: 'My Profile',
    icon: '👤',
    path: '/profile',
    accentColor: '#22C55E',
    roles: ['master_admin','admin','manager','estimator','employee',
            'vendor','client','auditor','foreman','field_worker','safety_officer','superintendent'],
  },
  {
    id: 'bid_request',
    name: 'Bid Request',
    icon: '📋',
    path: '/bid-request',
    accentColor: '#3B82F6',
    roles: ['foreman','employee','vendor','client'],
  },
  {
    id: 'timekeeping',
    name: 'Timekeeping',
    icon: '⏰',
    path: '/timekeeping',
    accentColor: '#3B82F6',
    roles: ['master_admin','admin','manager','employee','estimator',
            'foreman','field_worker','safety_officer','superintendent'],
  },
  {
    id: 'projects',
    name: 'Projects',
    icon: '📊',
    path: '/projects',
    accentColor: '#F97316',
    roles: ['master_admin','admin','manager','auditor','superintendent','foreman','safety_officer'],
  },
  {
    id: 'crm',
    name: 'CRM',
    icon: '🤝',
    path: '/crm',
    accentColor: '#6366F1',
    roles: ['master_admin','admin','manager','estimator'],
  },
  {
    id: 'hr',
    name: 'HR Management',
    icon: '👥',
    path: '/hr',
    accentColor: '#8B5CF6',
    roles: ['master_admin','admin'],
  },
  {
    id: 'bidworkup',
    name: 'Bid Workup',
    icon: '🏗️',
    path: '/bidworkup',
    accentColor: '#EAB308',
    roles: ['master_admin','admin','estimator','manager'],
  },
  {
    id: 'documents',
    name: 'Document Control',
    icon: '📄',
    path: '/documents',
    accentColor: '#06B6D4',
    roles: ['master_admin','admin','manager','estimator','superintendent'],
  },
  {
    id: 'compliance',
    name: 'Compliance',
    icon: '✓',
    accentColor: '#7C3AED',
    roles: ['master_admin','admin'],
    comingSoon: true,
  },
  {
    id: 'finance',
    name: 'Finance & Accounting',
    icon: '💰',
    path: '/finance',
    accentColor: '#16A34A',
    roles: ['master_admin','admin'],
  },
  {
    id: 'fleet',
    name: 'Fleet Management',
    icon: '🚛',
    path: '/fleet',
    accentColor: '#EF4444',
    roles: ['master_admin','admin','manager','superintendent','foreman'],
  },
  {
    id: 'forms',
    name: 'Forms',
    icon: '📝',
    path: '/forms',
    accentColor: '#0EA5E9',
    // Feature-gated (features_enabled.forms_platform) rather than role-gated — the filter below
    // shows this tile only when the tenant has the feature on; each surface self-gates by permission.
    roles: ['master_admin','admin','operations_administrator','principal','executive','operations_manager','manager','superintendent','foreman','timekeeper','field_worker','employee','project_engineer','safety_officer'],
  },
  // NO "CAPITAL PROJECTS" TILE (owner, 30 Sep 2026: Project Financials is the one place for capital work).
  // Its board and estimates live in Project Financials, and a capital project's award baseline is on the
  // project's cost page there; an old /capital address forwards (modules/capital/CapitalModule.jsx). The
  // `capital` module SWITCH stays: the capital estimate's server routes still answer only with it on.
  {
    id: 'capex',
    name: 'Project Financials',
    icon: '📉',
    path: '/capex',
    accentColor: '#7C3AED',
    // A SHORTER LIST THAN THE OLD CAPITAL PROJECTS TILE HAD, on purpose. Every route in the capex app requires
    // `project:view_financial` (tenbar_capex/deps.py:DEFAULT_CAPEX_PERMISSION) because every page
    // of it is the job's money. Of the seeded roles only `admin` and `accounting` hold that, so the
    // tile stops there — a tile that opens onto a permission wall is worse than no tile.
    //
    // `master_admin` is included because it bypasses permission checks entirely, so the room really
    // is open to it. (`manager` was left off at first: it has `capital:read` and does NOT have
    // `project:view_financial`; P2 below added it for the estimates.)
    //
    // As above, this array is the WHOLE gate for the tile — it never reads accessible_modules — so
    // a tenant granting a role both halves in the Roles editor gets the nav strip entry but not
    // this tile until the role is added here.
    //
    // PLAN-28 P2 WIDENED IT to the roles that build capital estimates, because the estimates live
    // here now. `estimator` and `manager` hold `bid:view_bids`, which is what the board's estimate
    // half is served on (Bid Workup, /capital-estimates); the job-money half still needs
    // `project:view_financial`, and the page shows it only to those who hold it. So an estimator's
    // tile opens onto the estimates, not onto a wall — the rule above, kept. (The paragraph above
    // about `manager` predates this: a manager holds both halves.) Mirrors identity's seed and
    // scripts/migrations/2026_11_12_capex_door_for_estimators.sql.
    //
    // CAPITAL'S DOOR MOVED HERE (owner, 30 Sep 2026: the Capital Projects tile is retired; Project
    // Financials is the one place for capital work). The four roles below had the Capital tile and not
    // this one, so retiring it would have taken away their only door to capital work. They get this tile
    // exactly where they had that one. A tile is a door, not a permission: what they can open inside is
    // still the servers' decision (project:view_financial for the job money, bid permissions for the
    // estimates). Their nav strip entry was never missing (ROLE_NAV_DEFAULTS in modules.js lists 'capex'
    // for all four); this array was the only thing hiding it.
    roles: ['master_admin','admin','accounting','manager','estimator',
      'operations_administrator','principal','executive','operations_manager'],
  },
  {
    id: 'invoicing',
    name: 'Invoicing',
    icon: '🧾',
    path: '/invoicing',
    accentColor: '#0E7490',
    // Capex's twin: every route requires `project:view_financial`
    // (tenbar_invoicing/deps.py:DEFAULT_INV_PERMISSION) because every page of it is billing —
    // contract values, the G702 grid, receivables, cash. Same three roles, same reasoning as the
    // capex tile above; a tenant widens it by granting both halves in the Roles editor and adding
    // the role here.
    roles: ['master_admin','admin','accounting'],
  },
  {
    // PURCHASING (Capex completion plan C5, docs/PURCHASING.md): purchase orders on Project Financials jobs,
    // receiving, vendor invoices matched, the company vendor list. It opens Capex's own Purchasing page, which
    // a receiver or a buyer without project money can open too; what each can do there is the finance
    // service's decision (procurement:* and finance:manage_invoices). The tile follows Project Financials'
    // switch (modules.js SWITCHED_WITH). `roles` is the fallback only: a real login's accessible_modules
    // decides (scripts/migrations/2026_12_53_purchasing_permissions.sql gives the module to these roles).
    id: 'procurement',
    name: 'Procurement',
    icon: '🛒',
    path: '/capex/purchasing',
    accentColor: '#B45309',
    roles: ['master_admin','admin','manager','accounting','superintendent','foreman'],
  },
]

/* ═══════════════════════════════════════════════════════════════════════
   Quick-action shortcuts — one per accessible module.
   Roles inherit actions from their module access, not hardcoded role names.
═══════════════════════════════════════════════════════════════════════ */

// `label` is the ENGLISH FALLBACK; `key` is the id in the `portal` dictionary. These are NOT the
// module names — "Submit Timesheet" is a verb phrase about what the button does — so they stay here
// rather than in dict/*/modules.js.
const QUICK_ACTION_MAP = {
  timekeeping: { key: 'qaTimekeeping', label: 'Submit Timesheet',   icon: '⏰', path: '/timekeeping',        color: 'bg-blue-50   border-blue-200   text-blue-800   hover:bg-blue-100' },
  bid_request: { key: 'qaBidRequest',  label: 'New Bid Request',     icon: '📋', path: '/bid-request',        color: 'bg-sky-50    border-sky-200    text-sky-800    hover:bg-sky-100' },
  bidworkup:   { key: 'qaBidWorkup',   label: 'Open Bid Workup',     icon: '🏗️', path: '/bidworkup',         color: 'bg-yellow-50 border-yellow-200 text-yellow-800 hover:bg-yellow-100' },
  projects:    { key: 'qaProjects',    label: 'View Projects',       icon: '📊', path: '/projects',           color: 'bg-orange-50 border-orange-200 text-orange-800 hover:bg-orange-100' },
  hr:          { key: 'qaHr',          label: 'HR Dashboard',        icon: '👥', path: '/hr',                 color: 'bg-purple-50 border-purple-200 text-purple-800 hover:bg-purple-100' },
  crm:         { key: 'qaCrm',         label: 'CRM Dashboard',       icon: '🤝', path: '/crm',               color: 'bg-indigo-50 border-indigo-200 text-indigo-800 hover:bg-indigo-100' },
  documents:   { key: 'qaDocuments',   label: 'Document Control',    icon: '📄', path: '/documents',          color: 'bg-cyan-50   border-cyan-200   text-cyan-800   hover:bg-cyan-100' },
  fleet:       { key: 'qaFleet',       label: 'Fleet Dashboard',     icon: '🚛', path: '/fleet',              color: 'bg-red-50    border-red-200    text-red-800    hover:bg-red-100' },
  finance:     { key: 'qaFinance',     label: 'Finance',             icon: '💰', path: '/finance',            color: 'bg-green-50  border-green-200  text-green-800  hover:bg-green-100' },
}

/**
 * One of the notifications panel's two reads, reported as the thing it actually is.
 *
 * THREE STATES, NOT TWO. "Nothing is waiting" and "we could not tell" are different answers, and
 * this component is the second one. It renders ABOVE whatever did load rather than in place of it,
 * so a half-answer is never presented as a whole one — a partial list shown as complete is the same
 * lie in smaller print.
 *
 * A BOUNDARY IS NOT AN ALARM. A 403 here is the ordinary, correct shape of a role that was never
 * meant to read bid-workup notices, so it gets grey and a padlock rather than amber and a warning
 * triangle; painting it red teaches people that the dashboard is broken every time they sign in. The
 * sentence is the server's own wherever the backend wrote one — `describeFailure` prefers
 * `response.data.detail`, which names the permission and says who to ask — and it is worded in the
 * PRESENT tense on purpose, because `/auth/refresh` re-mints permissions from the database every 15
 * minutes and a grant made elsewhere does reach this tab. "You cannot do this" is true; "you can
 * never do this" is not.
 *
 * RETRY IS GATED ON `retryable` AND NOTHING ELSE, which `describeFailure` sets for `transient` and
 * for no other kind. Every one of this backend's 403s is decided from JWT claims with no database
 * round-trip, so a Retry beside one is a control that can only fail.
 */
function SourceFailure({ source, failure, onRetry, retrying }) {
  const boundary = failure.kind === 'boundary'
  return (
    <div
      className={`flex items-start gap-2.5 rounded-lg border px-3 py-2.5 mb-3 ${
        boundary
          ? 'bg-gray-50 border-gray-200 text-gray-600'
          : 'bg-amber-50 border-amber-200 text-amber-900'
      }`}
    >
      <span className="text-base leading-none mt-0.5">{boundary ? '🔒' : '⚠️'}</span>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-semibold">{source}</p>
        <p className="text-xs mt-0.5">{failure.message}</p>
        {failure.retryable && (
          <button
            onClick={onRetry}
            disabled={retrying}
            className="mt-2 text-xs font-medium px-2.5 py-1 rounded border border-amber-300 bg-white text-amber-900 hover:bg-amber-100 disabled:opacity-60 disabled:cursor-default transition-colors"
          >
            {retrying ? PANEL_FAILURE_COPY.retrying : PANEL_FAILURE_COPY.retry}
          </button>
        )}
      </div>
    </div>
  )
}

/* ═══════════════════════════════════════════════════════════════════════
   Portal — main dashboard component
═══════════════════════════════════════════════════════════════════════ */

const Portal = () => {
  const { user }    = useAuth()
  const navigate    = useNavigate()
  const t           = useT('portal')
  // The bell's own words are AppHeader's, and the two render side by side on this screen — a daily
  // report that says one thing in the header dropdown and another in this panel reads as two
  // different notifications about two different reports.
  const tchrome     = useT('chrome')
  const tm          = useT('modules')
  const { lang }    = useLanguage()
  const { data, loading, moduleIds, isAdmin, refresh, lastRefresh } = useDashboard(user)

  // Notifications — merge pending daily reports + bid workup + timekeeping context
  const { notifications: tkNotifs, markAsRead: tkMarkAsRead, markAllAsRead: tkMarkAllRead } = useNotificationCenter()
  const [bwNotifs, setBwNotifs]               = useState([])
  const [pendingReports, setPendingReports]   = useState([])
  const [notifLoading, setNotifLoading]       = useState(true)
  /**
   * Why each read carries a FAILURE slot of its own instead of sharing one flag.
   *
   * These are two different things: bid-workup notices, and the daily reports waiting on an
   * approver. A role can be entitled to one and refused the other — a 403 on bid-workup notices is
   * the ordinary shape of a superintendent's session — so a single "notifications failed" flag would
   * either hide a live refusal or condemn a panel that is three-quarters correct. Held apart, the
   * panel can name exactly which half is missing.
   */
  const [bwFailure, setBwFailure]             = useState(null)
  const [reportsFailure, setReportsFailure]   = useState(null)
  /** Which source's Retry is in flight, so that one button can say so without blanking the panel. */
  const [retrying, setRetrying]               = useState(null)

  /**
   * Bid-workup notices.
   *
   * On failure the list is LEFT AS IT WAS rather than cleared. On first load that is the empty array
   * it started as, so nothing is invented; on a retry it keeps notices read off an earlier
   * successful response, which are still true — a refresh that did not land is not evidence that
   * they were withdrawn. The banner above them says the list may be short, which is the honest
   * claim to make about it.
   */
  const loadBidWorkupNotifs = useCallback(async () => {
    try {
      const d = await bidWorkupService.getNotifications()
      setBwNotifs(d?.notifications || [])
      setBwFailure(null)
    } catch (err) {
      setBwFailure(describeFailure(err, PANEL_FAILURE_COPY.bidWorkupFallback))
    }
  }, [])

  /**
   * The daily reports waiting on somebody — the read this whole change exists for.
   *
   * THE DEFECT. This catch was `catch {}` and `setNotifLoading(false)` ran unconditionally after it,
   * so a 500, a 403, or a timekeeping container restarting mid-request left `pendingReports` at `[]`
   * and the panel fell straight into its `notifications.length === 0` branch: "No new
   * notifications". For an APPROVER that is a confident, wrong statement that no work is waiting on
   * them. The same `[]` reached the Timekeeping KPI widget as a flat "0 Awaiting approval", so the
   * screen said it twice. A read that failed is not permission to state a negative.
   *
   * `readStoredUser()` is called OUTSIDE the try on purpose — see its header.
   */
  // TIMEKEEPING SWITCHED OFF for this company (Super Admin's module switches): its daily reports are
  // not asked for at all. Asking got the switch's 404 and drew an "Approvals / Not Found" box on every
  // portal load (review APP-1); with the module off there is nothing to approve, so that half of the
  // panel is simply absent.
  const timekeepingOn = isModuleReleased('timekeeping', useModuleSwitches().switches)
  const loadPendingReports = useCallback(async () => {
    if (!timekeepingOn) {
      setPendingReports([])
      setReportsFailure(null)
      return
    }
    const u = readStoredUser()
    const role = (u.role || '').toLowerCase()
    try {
      const isForemanRole = ['foreman', 'field_worker', 'employee'].includes(role)
      const params = { limit: 200 }
      if (isForemanRole) {
        const empId = u.employee_id ?? u.id
        if (empId) params.foreman_id = empId
      }
      const d = await timekeepingService.getDailyReports(params)
      const items = d?.items ?? []
      const pending = items.filter(r => ['submitted', 'timekeeper_reviewed'].includes(r.status))
      const relevant = APPROVAL_ROLES.includes(role)
        ? pending
        : pending.filter(r => r.foreman_id === u.id || r.foreman_id === u.employee_id)
      setPendingReports(relevant)
      setReportsFailure(null)
    } catch (err) {
      setReportsFailure(describeFailure(err, PANEL_FAILURE_COPY.reportsFallback))
    }
  }, [timekeepingOn])

  useEffect(() => {
    setNotifLoading(true)
    // Neither loader can reject — each ends in its own catch — so this cannot strand the panel on
    // the skeleton. They run CONCURRENTLY where this used to await one and then the other: they are
    // independent reads of two services, and a slow bid-workup call was delaying the approvals the
    // reader is actually here for. There is no polling on this panel — these deps never change — so
    // a visible failure state settles instead of flickering. (The byte-identical read in AppHeader
    // IS on a 60s interval, which is why it is left alone.)
    Promise.all([loadBidWorkupNotifs(), loadPendingReports()]).finally(() => setNotifLoading(false))
  }, [loadBidWorkupNotifs, loadPendingReports])

  /**
   * Re-run ONE source.
   *
   * Reachable only from a Retry control, and a Retry control is drawn only where `failure.retryable`
   * is true — which is `transient` and nothing else. Retrying one source rather than both keeps the
   * half that already answered on screen while the other is re-read.
   */
  const retrySource = useCallback(async (source) => {
    setRetrying(source)
    try {
      await (source === 'reports' ? loadPendingReports() : loadBidWorkupNotifs())
    } finally {
      setRetrying(null)
    }
  }, [loadBidWorkupNotifs, loadPendingReports])

  // Normalize all sources into a single sorted list
  const notifications = [
    // Pending daily reports (always unread action items)
    ...(Array.isArray(pendingReports) ? pendingReports : []).map(r => ({
      id: `dr-${r.id}`,
      _source: 'daily_report',
      _origId: r.id,
      title: `${r.foreman_name || tchrome('dailyReport')} — ${new Date(r.report_date + 'T00:00:00').toLocaleDateString(lang, { month: 'short', day: 'numeric', year: 'numeric' })}`,
      body: r.status === 'timekeeper_reviewed' ? tchrome('reviewedNeedsApproval') : tchrome('submittedNeedsReview'),
      category: 'action_required',
      is_read: false,
      created_at: r.submitted_at || r.updated_at || r.report_date,
    })),
    // Timekeeping context notifications
    ...(Array.isArray(tkNotifs) ? tkNotifs : []).map(n => ({
      id: `tk-${n.id}`,
      _source: 'timekeeping',
      _origId: n.id,
      title: n.title || n.message || t('timekeepingNotification'),
      body: n.body || n.details || '',
      category: n.type || 'info',
      is_read: n.is_read,
      created_at: n.created_at,
      report_id: n.report_id,
      field_ticket_id: n.field_ticket_id,
    })),
    ...(Array.isArray(bwNotifs) ? bwNotifs : []).map(n => ({
      id: `bw-${n.id}`,
      _source: 'bidworkup',
      _origId: n.id,
      title: n.title,
      body: n.body,
      category: n.category,
      is_read: n.is_read,
      created_at: n.created_at,
      bid_request_id: n.bid_request_id,
      link: n.link,
    })),
  ].sort((a, b) => parseUTC(b.created_at) - parseUTC(a.created_at))

  const notifUnread = notifications.filter(n => !n.is_read).length

  // Which of the two reads did not answer, named. Drives the banners and the three-way choice below
  // between a list, a partial "nothing else", and the "none yet" state.
  //
  // ⚠ THE UNREAD BADGE IS COUNTED OFF `notifications`, so while a source is missing that badge is an
  // UNDER-count and there is no honest number to put in its place. That is exactly why the banner
  // lives INSIDE the panel rather than being a toast: the caveat has to stay next to the number it
  // qualifies.
  const failedSources = [
    reportsFailure ? PANEL_FAILURE_COPY.approvals : null,
    bwFailure ? PANEL_FAILURE_COPY.bidWorkup : null,
  ].filter(Boolean)

  const markAllRead = async () => {
    await tkMarkAllRead()
    await bidWorkupService.markAllNotificationsRead().catch(() => {})
    setBwNotifs(ns => ns.map(n => ({ ...n, is_read: 1 })))
  }

  const handleNotifClick = async (n) => {
    if (!n.is_read) {
      if (n._source === 'timekeeping') {
        tkMarkAsRead([n._origId])
      } else if (n._source === 'bidworkup') {
        bidWorkupService.markNotificationRead(n._origId).catch(() => {})
        setBwNotifs(ns => ns.map(x => x.id === n._origId ? { ...x, is_read: 1 } : x))
      }
      // daily_report items don't have a "mark read" — they clear when approved
    }
    // Navigate based on source
    if (n._source === 'daily_report') {
      const u = readStoredUser()
      const role = (u.role || '').toLowerCase()
      const canReview = ['master_admin', 'admin', 'manager', 'superintendent', 'timekeeper'].includes(role)
      navigate(canReview ? `/timekeeping/review/${n._origId}` : `/timekeeping/report/${n._origId}`)
    } else if (n._source === 'timekeeping') {
      if (n.report_id) {
        navigate(`/timekeeping/report/${n.report_id}`)
      } else if (n.field_ticket_id) {
        navigate(`/projects/ticket/${n.field_ticket_id}`)
      } else {
        navigate('/timekeeping')
      }
    } else {
      if (n.bid_request_id) {
        navigate('/bidworkup/requests')
      } else {
        // THE SAME RULE THE HEADER BELL USES. This panel navigated on the RAW stored link, so it
        // carried both defects the header bell had: it did not repair the two legacy shapes that
        // resolve to nothing (`/bid-workup/{id}` lands on /dashboard, `/finance/invoices/{id}` on
        // the finance dashboard), and it had no guard — a stored `//evil.test/x` starts with a
        // slash and takes the browser off the site.
        //
        // Two panels showing the same rows must not disagree about where a row goes. Found because
        // the owner sent a screenshot of this screen running 2.99.48: I had fixed the header bell
        // without knowing this second surface existed.
        const route = routeForNotification(n)
        if (route) navigate(route)
      }
    }
  }

  // Which KPI widgets to render (driven by moduleIds from useDashboard)
  const showTimekeeping = moduleIds.includes('timekeeping')
  const showProjects    = moduleIds.includes('projects')
  const showBidWorkup   = moduleIds.includes('bidworkup') || moduleIds.includes('bid_request')
  const showHR          = moduleIds.includes('hr')
  const showCRM         = moduleIds.includes('crm')
  const widgetCount     = [showTimekeeping, showProjects, showBidWorkup, showHR, showCRM, isAdmin]
    .filter(Boolean).length

  const userRole = user?.role?.toLowerCase() || 'user'
  const { features } = useTenantBranding()
  const { switches } = useModuleSwitches()

  const userDisplayName = user?.first_name && user?.last_name
    ? `${user.first_name} ${user.last_name}`
    : user?.email || t('user')

  const initials = user?.first_name && user?.last_name
    ? `${user.first_name[0]}${user.last_name[0]}`.toUpperCase()
    : (user?.email?.[0] ?? 'U').toUpperCase()

  // The date is formatted in the READER's language, not in the application's. A Spanish screen that
  // says "Monday, August 11" has switched language halfway through a sentence.
  const today = new Date().toLocaleDateString(lang, {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
  })
  const hour     = new Date().getHours()
  const greeting = hour < 12 ? t('goodMorning') : hour < 17 ? t('goodAfternoon') : t('goodEvening')

  // Nav tiles — role-filtered module list. Feature-gated modules (e.g. Forms) show only when the
  // tenant has the feature on, independent of the role map.
  //
  // A HELD module (Capital, Capex, Invoicing: shared/constants/modules.js HELD_MODULES) has no tile until
  // the server's switch releases it, and that is checked FIRST: the master_admin short-circuit below
  // would otherwise draw all three for every admin on a production that refuses them.
  // Finding 5: the migration that widens who reaches Capex keys on the PERMISSION a company granted
  // (`role_module_defaults`, which login fills onto `user.accessible_modules` — the same table
  // `getAccessibleNavModules` reads for the nav strip). This filter used to key on a hardcoded ROLE
  // NAME instead, so a tenant's own "Chief Estimator" role — granted the tab by the migration because
  // it holds bid:view_bids — got the nav strip entry but never this tile, which disagreed with itself
  // on the same screen. `user.accessible_modules` is the SAME rule the grant follows, so this now asks
  // it first, exactly as the nav strip already does. master_admin keeps its short-circuit (it bypasses
  // every permission check, so the room really is open to it regardless of what accessible_modules
  // says); everyone else falls back to the role array only when the server sent no explicit list at
  // all — which keeps every seeded-role test byte-identical, since accessible_modules is what a real
  // login always fills for a role that has module rows (modules.js's own note on `ROLE_NAV_DEFAULTS`).
  // The server's list is read through grantedModuleIds (shared/constants/modules.js), the same reading
  // the nav strip uses - including its rule that a 'capital' grant opens Project Financials (owner, 30 Sep
  // 2026: the Capital Projects tile is retired).
  const granted = grantedModuleIds(user)
  const accessibleModules = ALL_MODULES.filter(m => {
    if (!isModuleReleased(m.id, switches)) return false
    if (FEATURE_GATED[m.id]) return Boolean(features?.[FEATURE_GATED[m.id]])
    if (userRole === 'master_admin') return true
    if (granted) return granted.includes(m.id)
    return m.roles.includes(userRole)
  })

  // Quick actions — one per module the user can access (cap at 6)
  const quickActions = Object.entries(QUICK_ACTION_MAP)
    .filter(([id]) => accessibleModules.some(m => m.id === id))
    .map(([, action]) => action)
    .slice(0, 9)

  // Which KPI widgets to render (moved above — already declared)

  return (
    <div className="min-h-screen bg-transparent animate-fade-in">
      <AppHeader
        title={t('dashboard')}
        showPortalButton={false}
        user={user}
      />

      <main className="px-4 sm:px-6 lg:px-8 py-8 space-y-8">

        {/* ── Greeting row ─────────────────────────────────────────────── */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              {greeting}, {user?.first_name || userDisplayName}
            </h2>
            <p className="text-sm text-gray-500 mt-0.5">{today}</p>
          </div>
          <button
            onClick={refresh}
            className="text-xs px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 shadow-sm transition-colors"
          >
            ↻ {t('refresh')}
            {lastRefresh && (
              <span className="text-gray-400 ml-1">
                · {lastRefresh.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </span>
            )}
          </button>
        </div>

        {/* ── Profile card + Notifications ─────────────────────────────── */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">

          {/* Profile card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-4 mb-5">
              <div
                className="w-14 h-14 rounded-full flex items-center justify-center text-xl font-bold text-white shrink-0 select-none"
                style={{ background: 'linear-gradient(135deg, var(--secondary-color) 0%, var(--primary-color) 100%)' }}
              >
                {initials}
              </div>
              <div className="min-w-0">
                <p className="font-semibold text-gray-900 truncate">{userDisplayName}</p>
                <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                <span className="inline-block mt-1 px-2 py-0.5 text-xs font-semibold bg-gray-100 text-gray-700 rounded-full uppercase tracking-wide">
                  {userRole.replace(/_/g, ' ')}
                </span>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => navigate('/profile')}
                className="flex-1 text-xs py-2 px-3 bg-gray-800 text-white rounded-lg hover:bg-gray-700 font-medium transition-colors"
              >
                {t('editProfile')}
              </button>
              {isAdmin && (
                <button
                  onClick={() => navigate('/admin')}
                  className="flex-1 text-xs py-2 px-3 border border-red-800 text-red-800 rounded-lg hover:bg-red-50 font-medium transition-colors"
                >
                  {t('adminPanel')}
                </button>
              )}
            </div>
          </div>

          {/* Notifications panel */}
          <div className="md:col-span-2 bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                🔔 {t('notifications')}
                {notifUnread > 0 && (
                  <span className="inline-flex items-center justify-center w-5 h-5 text-xs bg-red-500 text-white rounded-full font-bold">
                    {notifUnread > 99 ? '99+' : notifUnread}
                  </span>
                )}
              </h3>
              {notifUnread > 0 && (
                <button onClick={markAllRead} className="text-xs text-blue-600 hover:underline">{t('markAllRead')}</button>
              )}
            </div>
            {notifLoading ? (
              <div className="space-y-3">
                {[0,1,2].map(i => (
                  <div key={i} className="animate-pulse flex gap-3">
                    <div className="w-8 h-8 rounded-full bg-gray-200 shrink-0" />
                    <div className="flex-1 space-y-1.5 py-1">
                      <div className="h-3 bg-gray-200 rounded w-3/4" />
                      <div className="h-2.5 bg-gray-100 rounded w-1/2" />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <>
                {/* A source that did not answer is reported ABOVE the rows, never instead of them.
                    If bid workup is refused and the approvals read succeeded, those approvals still
                    belong on screen — with a line saying what is missing from the list beside them. */}
                {reportsFailure && (
                  <SourceFailure
                    source={PANEL_FAILURE_COPY.approvals}
                    failure={reportsFailure}
                    onRetry={() => retrySource('reports')}
                    retrying={retrying === 'reports'}
                  />
                )}
                {bwFailure && (
                  <SourceFailure
                    source={PANEL_FAILURE_COPY.bidWorkup}
                    failure={bwFailure}
                    onRetry={() => retrySource('bidworkup')}
                    retrying={retrying === 'bidworkup'}
                  />
                )}
                {notifications.length > 0 ? (
                  <div className="divide-y divide-gray-50 -mx-6 max-h-64 overflow-y-auto">
                    {notifications.slice(0, 10).map(n => (
                      <div
                        key={n.id}
                        onClick={() => handleNotifClick(n)}
                        className={`flex items-start gap-3 px-6 py-3 cursor-pointer hover:bg-gray-50 transition-colors ${ !n.is_read ? 'bg-blue-50/60' : '' }`}
                      >
                        <span className="text-lg mt-0.5">{
                          n._source === 'daily_report'
                            ? '⚠️'
                            : n._source === 'timekeeping'
                            ? (n.category === 'flagged' || n.category === 'rejected' ? '⚠️'
                              : n.category === 'submitted' ? '📝'
                              : n.category === 'approved' ? '✅'
                              : '⏱️')
                            : (n.category === 'status_change' ? '✅' : n.category === 'comment' ? '💬' : '📋')
                        }</span>
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm ${ !n.is_read ? 'font-semibold text-gray-900' : 'text-gray-700' }`}>{n.title}</p>
                          {n.body && <p className="text-xs text-gray-500 truncate">{n.body}</p>}
                          <p className="text-xs text-gray-400 mt-0.5">{formatLocalDateTime(n.created_at)}</p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0 mt-1">
                          {!n.is_read && <div className="w-2 h-2 rounded-full bg-blue-500" />}
                          <span className="text-gray-300 text-sm">›</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : failedSources.length === 2 ? (
                  /* Nothing answered, so there is nothing to be empty ABOUT. The two banners above
                     are the whole content of the panel. */
                  <p className="text-sm text-gray-600 text-center py-6">{PANEL_FAILURE_COPY.noneKnown}</p>
                ) : failedSources.length === 1 ? (
                  /* One source answered with nothing and the other did not answer at all. Saying
                     "No new notifications" here would quietly promote a half-read to a whole one. */
                  <p className="text-sm text-gray-600 text-center py-6">
                    {PANEL_FAILURE_COPY.partialEmpty(failedSources[0])}
                  </p>
                ) : (
                  /* THE ONLY BRANCH ENTITLED TO SAY "none". Both reads answered and both were empty,
                     which makes this a fact about the work rather than a fact about the network. */
                  <div className="flex flex-col items-center justify-center py-8 text-center">
                    <div className="text-4xl mb-3">📭</div>
                    <p className="text-sm font-medium text-gray-600">{t('noNewNotifications')}</p>
                    <p className="text-xs text-gray-400 mt-1 max-w-xs">
                      {t('notificationsHint')}
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* ── KPI Widgets ──────────────────────────────────────────────── */}
        {widgetCount > 0 && (
          <div>
            <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">
              {t('kpiSummary')}
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* `null`, not `pendingReports.length`, when that read failed: `[]` is indistinguishable
                  from "none waiting" and the widget would print a flat 0 at an approver. */}
              {showTimekeeping && <TimekeepingWidget data={data} navigate={navigate} pendingReportsCount={reportsFailure ? null : pendingReports.length} />}
              {showProjects    && <ProjectsWidget    data={data} navigate={navigate} />}
              {showBidWorkup   && <BidWorkupWidget   data={data} navigate={navigate} />}
              {showHR          && <HRWidget          data={data} navigate={navigate} />}
              {showCRM         && <CRMWidget         data={data} navigate={navigate} />}
              {isAdmin         && <AdminUsersWidget  data={data} navigate={navigate} />}
            </div>
          </div>
        )}

        {/* ── Quick Actions ──────────────────────────────────────────── */}
        {quickActions.length > 0 && (
          <div>
            <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">
              {t('quickActions')}
            </h3>
            <div className="flex flex-wrap gap-3">
              {quickActions.map(a => (
                <button
                  key={a.path}
                  onClick={() => navigate(a.path)}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border text-sm font-medium transition-colors ${a.color}`}
                >
                  <span>{a.icon}</span>
                  {t(a.key) || a.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ── Module Navigation ─────────────────────────────────────── */}
        <div>
          <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">
            {t('modules')}
          </h3>
          {accessibleModules.length === 0 ? (
            <div className="text-center py-10 bg-white rounded-xl border border-gray-200">
              <p className="text-gray-600 font-medium">{t('noModules')}</p>
              <p className="text-gray-400 text-sm mt-1">{t('noModulesHelp')}</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
              {accessibleModules.map(m => (
                <button
                  key={m.id}
                  onClick={() => {
                    if (m.comingSoon) { alert(t('comingSoon', tm(m.id) || m.name)); return }
                    if (m.path) navigate(m.path)
                  }}
                  disabled={m.comingSoon}
                  className={`bg-white rounded-xl border border-gray-200 p-4 text-left transition-all
                    ${m.comingSoon
                      ? 'opacity-50 cursor-not-allowed'
                      : 'hover:shadow-md hover:border-gray-300 cursor-pointer active:scale-95'
                    }`}
                  style={!m.comingSoon ? { borderTop: `2px solid ${m.accentColor}` } : {}}
                >
                  <div className="text-2xl mb-2">{m.icon}</div>
                  <p className="text-xs font-semibold text-gray-800 leading-snug">{tm(m.id) || m.name}</p>
                  {m.comingSoon && (
                    <span className="mt-1 inline-block text-xs text-yellow-700 bg-yellow-100 px-1.5 py-0.5 rounded">
                      {t('soon')}
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

      </main>
    </div>
  )
}

export default Portal

